import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// Intersection Observer for lazy loading
export const useIntersectionObserver = (
  options: IntersectionObserverInit = {}
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [hasIntersected, options]);

  return { elementRef, isIntersecting, hasIntersected };
};

// Image lazy loading with progressive enhancement
export const useLazyImage = (src: string, placeholder?: string) => {
  const [imageSrc, setImageSrc] = useState(placeholder || '');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const { elementRef, hasIntersected } = useIntersectionObserver();

  useEffect(() => {
    if (!hasIntersected) return;

    const img = new Image();
    img.onload = () => {
      setImageSrc(src);
      setIsLoaded(true);
    };
    img.onerror = () => {
      setIsError(true);
    };
    img.src = src;
  }, [src, hasIntersected]);

  return {
    elementRef,
    imageSrc,
    isLoaded,
    isError,
    hasIntersected
  };
};

// Debounced search optimization
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Virtual scrolling for large lists
export const useVirtualScroll = <T>(
  items: T[],
  itemHeight: number,
  containerHeight: number,
  overscan: number = 5
) => {
  const [scrollTop, setScrollTop] = useState(0);
  const scrollElementRef = useRef<HTMLDivElement>(null);

  const visibleItems = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );

    return {
      startIndex,
      endIndex,
      items: items.slice(startIndex, endIndex + 1),
      totalHeight: items.length * itemHeight,
      offsetY: startIndex * itemHeight
    };
  }, [items, itemHeight, containerHeight, scrollTop, overscan]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  return {
    scrollElementRef,
    visibleItems,
    handleScroll
  };
};

// Resource preloading
export const useResourcePreloader = () => {
  const preloadedResources = useRef(new Set<string>());

  const preloadImage = useCallback((src: string) => {
    if (preloadedResources.current.has(src)) return;

    const img = new Image();
    img.src = src;
    preloadedResources.current.add(src);
  }, []);

  const preloadImages = useCallback((sources: string[]) => {
    sources.forEach(preloadImage);
  }, [preloadImage]);

  const preloadFont = useCallback((fontFamily: string, src: string) => {
    if (preloadedResources.current.has(src)) return;

    const font = new FontFace(fontFamily, `url(${src})`);
    font.load().then(() => {
      document.fonts.add(font);
      preloadedResources.current.add(src);
    });
  }, []);

  return { preloadImage, preloadImages, preloadFont };
};

// Memory management for large datasets
export const useMemoryOptimization = <T>(
  data: T[],
  maxCacheSize: number = 100
) => {
  const cache = useRef(new Map<string, T>());
  const accessOrder = useRef<string[]>([]);

  const getCachedItem = useCallback((key: string): T | undefined => {
    const item = cache.current.get(key);
    if (item) {
      // Move to end (most recently used)
      const index = accessOrder.current.indexOf(key);
      if (index > -1) {
        accessOrder.current.splice(index, 1);
      }
      accessOrder.current.push(key);
    }
    return item;
  }, []);

  const setCachedItem = useCallback((key: string, item: T) => {
    // Remove oldest items if cache is full
    while (cache.current.size >= maxCacheSize && accessOrder.current.length > 0) {
      const oldestKey = accessOrder.current.shift();
      if (oldestKey) {
        cache.current.delete(oldestKey);
      }
    }

    cache.current.set(key, item);
    accessOrder.current.push(key);
  }, [maxCacheSize]);

  const clearCache = useCallback(() => {
    cache.current.clear();
    accessOrder.current = [];
  }, []);

  return { getCachedItem, setCachedItem, clearCache };
};

// Performance monitoring
export const usePerformanceMonitor = () => {
  const [metrics, setMetrics] = useState({
    renderTime: 0,
    memoryUsage: 0,
    fps: 0
  });

  const measureRenderTime = useCallback((componentName: string) => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      setMetrics(prev => ({ ...prev, renderTime }));
      
      if (renderTime > 16) { // More than one frame at 60fps
        console.warn(`Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms`);
      }
    };
  }, []);

  const measureMemoryUsage = useCallback(() => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      setMetrics(prev => ({
        ...prev,
        memoryUsage: memory.usedJSHeapSize / 1024 / 1024 // MB
      }));
    }
  }, []);

  const measureFPS = useCallback(() => {
    let frames = 0;
    let lastTime = performance.now();

    const countFrames = () => {
      frames++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        setMetrics(prev => ({ ...prev, fps: frames }));
        frames = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(countFrames);
    };

    requestAnimationFrame(countFrames);
  }, []);

  useEffect(() => {
    measureFPS();
    const interval = setInterval(measureMemoryUsage, 5000);
    
    return () => clearInterval(interval);
  }, [measureFPS, measureMemoryUsage]);

  return { metrics, measureRenderTime };
};

// Bundle splitting and code splitting utilities
export const useDynamicImport = <T>(
  importFunction: () => Promise<{ default: T }>,
  fallback?: T
) => {
  const [component, setComponent] = useState<T | null>(fallback || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    importFunction()
      .then((module) => {
        if (isMounted) {
          setComponent(module.default);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [importFunction]);

  return { component, isLoading, error };
};

// Service Worker integration for caching
export const useServiceWorker = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      setIsSupported(true);
      
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          setIsRegistered(true);
          
          registration.addEventListener('updatefound', () => {
            setUpdateAvailable(true);
          });
        })
        .catch((error) => {
          // Handle service worker registration error silently in production
        if (process.env.NODE_ENV === 'development') {
          console.error('Service Worker registration failed:', error);
        }
        });
    }
  }, []);

  const updateServiceWorker = useCallback(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
        window.location.reload();
      });
    }
  }, []);

  return {
    isSupported,
    isRegistered,
    updateAvailable,
    updateServiceWorker
  };
};

// Critical resource hints
export const useResourceHints = () => {
  const addPreloadLink = useCallback((href: string, as: string, type?: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    document.head.appendChild(link);
  }, []);

  const addPrefetchLink = useCallback((href: string) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  }, []);

  const addDNSPrefetch = useCallback((hostname: string) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${hostname}`;
    document.head.appendChild(link);
  }, []);

  return { addPreloadLink, addPrefetchLink, addDNSPrefetch };
}; 